import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useUser } from '../../context/UserContext';
import { AI_FEEDBACK } from '../../data/mock';
import { Spark, Streak, Seg, Ticker, AiTag, ObsFeedback, Select } from '../ui';
import SectorRotationTab from './SectorRotationTab';
import RelativeStrengthTab, { clearRSCache } from './RelativeStrengthTab';
import NSEDirectoryTab from './NSEDirectoryTab';
import MacroEventsCalendarTab from './MacroEventsCalendarTab';
import MarketMoversTab from './MarketMoversTab';
import { getPerformanceTable } from '../../api/client';
import IconRefresh from '../ui/IconRefresh';
import { getTabReports, generateTabReport } from '../../api/ai';
import { useSidePanel } from '../../context/SidePanelContext';
import { INDEX_REGISTRY, KITE_TO_DISPLAY } from '../../data/indices';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours

// ── Module-level session cache (survives sub-tab switches, respects TTL) ──────
const _perfCache = {
  indices:  { data: null, ts: 0 },
  nifty100: { data: null, ts: 0 },
  fo:       { data: null, ts: 0 },
  assets:   { data: null, ts: 0 },
};
function _cacheGet(universe) {
  const e = _perfCache[universe];
  if (e.data && (Date.now() - e.ts) < CACHE_TTL_MS) return e.data;
  return null;
}
function _cacheSet(universe, data) {
  _perfCache[universe] = { data, ts: Date.now() };
}
function _cacheClear() {
  Object.keys(_perfCache).forEach(k => { _perfCache[k] = { data: null, ts: 0 }; });
}

// ── Measured heights ──────────────────────────────────────────────────────────
// NavBar:     44px (top row) + 25px (nav tabs) = 69px total
// Sub-tab row: 34px  → sticks at top:69
// Sub-tab bottom: 103px — this is where each table header freezes
// Jump-to bar: 32px → sticks at top:103, bottom: 135
// Each watchlist header freezes at top:135

const H_NAV      = 44;   // topbar height
const H_SUBTAB   = 34;   // sub-tab row height (visual height including border)
const TOP_SUBTAB = H_NAV;             // 44 — flush against nav bar (no gap)
const TOP_TABLE  = H_NAV + H_SUBTAB; // 78 — where table headers freeze
const TOP_JUMPBAR= H_NAV + H_SUBTAB; // 78 — jump-to bar in watchlists
const TOP_WL_HDR = TOP_JUMPBAR + 32; // 110 — watchlist headers freeze here

const PERIODS = ['1D','3D','5D','10D','15D','30D','90D'];
const COL_KEY  = {'1D':'d1','3D':'d3','5D':'d5','10D':'d10','15D':'d15','30D':'d30','90D':'d90'};
// SECTOR_LIST is now derived dynamically from row data inside PerfTable

function dateLabel(daysAgo) {
  if (daysAgo === 0) return 'Today';
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return `${d.getMonth()+1}/${d.getDate()}`;
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}); }
  catch { return iso; }
}

function heatStyle(v) {
  const op = Math.min(0.55, Math.abs(v)/3*0.55);
  if (v >= 0.05)  return { background:`rgba(26,106,26,${op.toFixed(2)})`,  color: op>0.35?'#e8f5e8':'#1a4a1a' };
  if (v <= -0.05) return { background:`rgba(136,30,16,${op.toFixed(2)})`, color: op>0.35?'#ffe8e8':'#4a0a00' };
  return { background:'var(--surface-2)', color:'var(--ink-3)' };
}

function HCell({ v }) {
  const s = heatStyle(v ?? 0);
  return (
    <td style={{...s, textAlign:'right', padding:'4px 6px',
      fontFamily:'var(--font-data)', fontSize:11, whiteSpace:'nowrap',
      borderBottom:'.5px solid var(--hair)'}}>
      {(v??0)>0?'+':''}{(v??0).toFixed(1)}%
    </td>
  );
}

function STH({ col, sort, onSort, children, style={} }) {
  const active = sort.col===col;
  return (
    <th onClick={()=>onSort(col)}
      style={{
        textAlign:'left', padding:'5px 6px', fontSize:11, fontWeight:700,
        letterSpacing:'.04em', textTransform:'uppercase', color:'var(--ink-3)',
        cursor:'pointer', userSelect:'none', whiteSpace:'nowrap',
        background:'var(--surface-2)', borderBottom:'.5px solid var(--hair)',
        ...style,
      }}>
      {children}
      <span style={{marginLeft:2,opacity:active?1:.3,fontSize:11,color:active?'var(--amber-ink)':'var(--ink-3)'}}>
        {active?(sort.dir>0?'↑':'↓'):'↕'}
      </span>
    </th>
  );
}

// ── PerfTable ─────────────────────────────────────────────────────────────────
// onToggle: if provided, the section title becomes a collapse button (▾ triangle)
function PerfTable({ rows, showVsNifty=true, sectorFilter=false, sectionTitle, headerTop=TOP_TABLE, headerZIndex=40, onRowClick=null, onRemoveRow=null, onToggle=null, stickyTitle=null, extraControls=null }) {
  const [view,    setView]   = useState('period');
  const [active,  setActive] = useState([...PERIODS]);
  const [custom,  setCustom] = useState('');
  const [winSize, setWinSize]= useState(15);
  const [sector,  setSector] = useState('All');
  const [search,  setSearch] = useState('');
  const [sort,    setSort]   = useState({col:'name',dir:1});

  // Refs for keeping the frozen header table in sync with body horizontal scroll
  const headerScrollRef = useRef(null);
  const bodyScrollRef   = useRef(null);

  const onSort = col => setSort(s => s.col===col ? {col,dir:-s.dir} : {col,dir:1});
  const togglePeriod = p => setActive(a => a.includes(p) ? a.filter(x=>x!==p) : [...a,p]);

  const filtered = useMemo(() => {
    let r = [...rows];
    if (sectorFilter && sector!=='All') r = r.filter(x => x.sector===sector);
    if (search) r = r.filter(x => x.name.toLowerCase().includes(search.toLowerCase()));
    return r.sort((a,b) => {
      const ak = sort.col==='name'?a.name : sort.col==='sector'?a.sector
               : sort.col==='streak'?parseInt(a.streak?.slice(1)||0) : a[sort.col]??0;
      const bk = sort.col==='name'?b.name : sort.col==='sector'?b.sector
               : sort.col==='streak'?parseInt(b.streak?.slice(1)||0) : b[sort.col]??0;
      if (typeof ak==='string') return ak.localeCompare(bk)*sort.dir;
      return (bk-ak)*sort.dir;
    });
  }, [rows, sector, search, sort]);

  // Build sector list dynamically from whatever sectors exist in the rows data
  const sectorList = useMemo(() => {
    if (!sectorFilter) return ['All'];
    const secs = [...new Set(rows.map(r => r.sector).filter(Boolean))].sort();
    return ['All', ...secs];
  }, [rows, sectorFilter]);

  // Reset selected sector if it no longer exists in the new list
  React.useEffect(() => {
    if (sector !== 'All' && !sectorList.includes(sector)) setSector('All');
  }, [sectorList, sector]);

  const shownPeriods = active.filter(p => PERIODS.includes(p));

  // Derive daily column headers from the actual trading dates in the closes data.
  // calcDaily() carries the real date on each entry so we never show "Today" on a
  // weekend or "5/1" on a holiday — the label always matches the actual close date.
  const dailyDateLabels = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    for (const row of filtered) {
      if (row.daily && row.daily.length > 0) {
        return row.daily.slice(0, winSize).map(d => {
          if (!d?.date) return '?';
          if (d.date === today) return 'Today';
          const [, mo, dy] = d.date.split('-');
          return `${parseInt(mo)}/${parseInt(dy)}`;
        });
      }
    }
    // Fallback: count backwards in calendar days (old behaviour — rarely used)
    return Array.from({ length: winSize }, (_, i) => dateLabel(i));
  }, [filtered, winSize]);

  const W = { streak:48, trend:60, name:110, sector:130 };
  const leftSector = W.streak + W.trend + W.name;

  // Minimum table width — prevents data columns from shrinking below their
  // natural minimum (44px daily / 52px period) while keeping left columns
  // pinned at their exact pixel widths regardless of viewport size.
  // When this exceeds the container width the body div overflows → scrollbar.
  const tableMinWidth = useMemo(() => {
    const lw = W.streak + W.trend + W.name + (sectorFilter ? W.sector : 0);
    if (view === 'daily') return lw + winSize * 44;
    return lw
      + shownPeriods.length * 52
      + (active.includes('custom') && custom ? 52 : 0)
      + (showVsNifty ? 68 : 0);
  }, [view, sectorFilter, winSize, shownPeriods, active, custom, showVsNifty]);

  const thBase = {
    background:'var(--surface-2)',
    borderBottom:'.5px solid var(--hair)',
    fontSize:11, fontWeight:700, letterSpacing:'.04em',
    textTransform:'uppercase', color:'var(--ink-3)',
    padding:'5px 6px', whiteSpace:'nowrap',
  };

  return (
    <div style={{marginBottom:24}}>

      {/* ── STICKY HEADER UNIT ── sticks at headerTop as one block ── */}
      <div style={{
        position:'sticky', top:headerTop, zIndex:headerZIndex,
        backgroundColor:'var(--paper)',
      }}>

        {/* External sticky title (e.g. watchlist name+add row) passed from parent */}
        {stickyTitle}

        {/* Section title — clickable collapse toggle when onToggle is provided */}
        {sectionTitle && (
          onToggle ? (
            <button onClick={onToggle}
              style={{
                display:'flex', alignItems:'center', gap:5,
                width:'100%', background:'none', border:'none', cursor:'pointer',
                padding:'6px 0 4px', borderBottom:'.5px solid var(--hair)',
                textAlign:'left',
              }}>
              <span style={{fontSize:11, fontWeight:700, letterSpacing:'.07em',
                textTransform:'uppercase', color:'var(--ink-3)'}}>
                {sectionTitle} ({filtered.length})
              </span>
              <span style={{fontSize:16, lineHeight:1, color:'var(--ink-2)'}}>▾</span>
            </button>
          ) : (
            <div style={{
              fontSize:11, fontWeight:700, letterSpacing:'.07em',
              textTransform:'uppercase', color:'var(--ink-3)',
              padding:'6px 0 4px',
              borderBottom:'.5px solid var(--hair)',
              background:'var(--paper)',
            }}>
              {sectionTitle} ({filtered.length})
            </div>
          )
        )}

        {/* Controls row */}
        <div style={{
          display:'flex', gap:6, alignItems:'center', flexWrap:'wrap',
          padding:'5px 0',
          borderBottom:'.5px solid var(--hair)',
          background:'var(--paper)',
        }}>
          <Seg opts={[{v:'period',l:'Period'},{v:'daily',l:'Daily'}]} val={view} onChange={setView}/>
          {sectorFilter && (
            <Select value={sector} onChange={e=>setSector(e.target.value)}>
              {sectorList.map(s=><option key={s}>{s}</option>)}
            </Select>
          )}
          <input className="inp" placeholder="Search..." value={search}
            onChange={e=>setSearch(e.target.value)} style={{minWidth:90}}/>
          {view==='period' && (
            <div style={{display:'flex',gap:3,flexWrap:'wrap',alignItems:'center'}}>
              {PERIODS.map(p=>(
                <button key={p} className={`pill${active.includes(p)?' on':' off'}`}
                  onClick={()=>togglePeriod(p)}>{p}</button>
              ))}
              <div style={{display:'inline-flex',alignItems:'center',gap:2,padding:'2px 6px',
                borderRadius:3,border:`.5px solid ${active.includes('custom')?'var(--amber-ink)':'var(--hair-strong)'}`,
                background:'var(--ibg)'}}>
                <button className={`pill${active.includes('custom')?' on':' off'}`}
                  style={{border:'none',padding:0,background:'transparent'}}
                  onClick={()=>setActive(a=>a.includes('custom')?a.filter(x=>x!=='custom'):[...a,'custom'])}>
                  Custom
                </button>
                <input className="inp" value={custom} onChange={e=>setCustom(e.target.value)}
                  style={{width:28,padding:'0 2px',border:'none',background:'transparent',fontSize:11,textAlign:'center'}}
                  placeholder="22"/>
                <span style={{fontSize:11,color:'var(--ink-3)'}}>D</span>
              </div>
            </div>
          )}
          {view==='daily' && (
            <div style={{display:'flex',alignItems:'center',gap:4}}>
              <span style={{fontSize:11,color:'var(--ink-3)'}}>Window:</span>
              {[10,15,20,30].map(w=>(
                <button key={w} className={`pill${winSize===w?' on':''}`}
                  onClick={()=>setWinSize(w)}>{w}D</button>
              ))}
            </div>
          )}
          {extraControls && <div style={{marginLeft:'auto'}}>{extraControls}</div>}
        </div>

        {/* Column headers — overflow:hidden so it scrolls only via the body's onScroll.
             The colgroup here MUST mirror the body table's colgroup exactly (same widths)
             so that tableLayout:fixed produces the same total table width in both, making
             the scrollLeft sync actually work. */}
        <div ref={headerScrollRef} style={{overflowX:'hidden', background:'var(--surface-2)', borderBottom:'.5px solid var(--hair)'}}>
          <table style={{width:'100%',minWidth:tableMinWidth,borderCollapse:'collapse',fontSize:11,tableLayout:'fixed'}}>
            <colgroup>
              <col style={{width:W.streak}}/>
              <col style={{width:W.trend}}/>
              <col style={{width:W.name}}/>
              {sectorFilter && <col style={{width:W.sector}}/>}
              {view==='period' && shownPeriods.map(p=><col key={`h${p}`}/>)}
              {view==='period' && active.includes('custom') && custom && <col key="hcustom"/>}
              {view==='period' && showVsNifty && <col key="hnifty"/>}
              {view==='daily' && Array.from({length:winSize},(_,i)=><col key={`hd${i}`}/>)}
            </colgroup>
            <thead>
              <tr>
                <th style={{...thBase, width:W.streak, minWidth:W.streak, textAlign:'center'}}>STREAK</th>
                <th style={{...thBase, width:W.trend,  minWidth:W.trend,  textAlign:'left'}}>TREND</th>
                <th style={{...thBase, width:W.name,   minWidth:W.name, textAlign:'left'}}
                  onClick={()=>onSort('name')} className={sort.col==='name'?'srt':''}>
                  NAME
                  <span style={{marginLeft:2,opacity:sort.col==='name'?1:.3,fontSize:11,color:sort.col==='name'?'var(--amber-ink)':'var(--ink-3)'}}>
                    {sort.col==='name'?(sort.dir>0?'↑':'↓'):'↕'}
                  </span>
                </th>
                {sectorFilter && (
                  <th style={{...thBase, width:W.sector, minWidth:W.sector, textAlign:'left'}}
                    onClick={()=>onSort('sector')} className={sort.col==='sector'?'srt':''}>
                    SECTOR
                    <span style={{marginLeft:2,opacity:sort.col==='sector'?1:.3,fontSize:11,color:sort.col==='sector'?'var(--amber-ink)':'var(--ink-3)'}}>
                      {sort.col==='sector'?(sort.dir>0?'↑':'↓'):'↕'}
                    </span>
                  </th>
                )}
                {view==='period' && shownPeriods.map(p=>(
                  <th key={p} style={{...thBase, textAlign:'right'}}
                    onClick={()=>onSort(COL_KEY[p])} className={sort.col===COL_KEY[p]?'srt':''}>
                    {p}
                    <span style={{marginLeft:2,opacity:sort.col===COL_KEY[p]?1:.3,fontSize:11,color:sort.col===COL_KEY[p]?'var(--amber-ink)':'var(--ink-3)'}}>
                      {sort.col===COL_KEY[p]?(sort.dir>0?'↑':'↓'):'↕'}
                    </span>
                  </th>
                ))}
                {view==='period' && active.includes('custom') && custom && (
                  <th style={{...thBase, textAlign:'right'}}>C{custom}D</th>
                )}
                {view==='period' && showVsNifty && (
                  <th style={{...thBase, textAlign:'right'}}
                    onClick={()=>onSort('vsNifty')} className={sort.col==='vsNifty'?'srt':''}>
                    vs NIFTY
                    <span style={{marginLeft:2,opacity:sort.col==='vsNifty'?1:.3,fontSize:11}}>
                      {sort.col==='vsNifty'?(sort.dir>0?'↑':'↓'):'↕'}
                    </span>
                  </th>
                )}
                {view==='daily' && Array.from({length:winSize},(_,i)=>(
                  <th key={i} style={{...thBase, textAlign:'right'}}>{dailyDateLabels[i] ?? '?'}</th>
                ))}
              </tr>
            </thead>
          </table>
        </div>
      </div>
      {/* ── END STICKY HEADER UNIT ── */}

      {/* ── TABLE BODY ── */}
      <div ref={bodyScrollRef}
        onScroll={e => { if (headerScrollRef.current) headerScrollRef.current.scrollLeft = e.target.scrollLeft; }}
        style={{overflowX:'auto', borderLeft:'.5px solid var(--hair)', borderRight:'.5px solid var(--hair)', borderBottom:'.5px solid var(--hair)', borderRadius:'0 0 10px 10px'}}>
        <table style={{width:'100%',minWidth:tableMinWidth,borderCollapse:'collapse',fontSize:11,tableLayout:'fixed'}}>
          <colgroup>
            <col style={{width:W.streak}}/>
            <col style={{width:W.trend}}/>
            <col style={{width:W.name}}/>
            {sectorFilter && <col style={{width:W.sector}}/>}
            {view==='period' && shownPeriods.map(p=><col key={p}/>)}
            {view==='period' && active.includes('custom') && custom && <col/>}
            {view==='period' && showVsNifty && <col/>}
            {view==='daily' && Array.from({length:winSize},(_,i)=><col key={i}/>)}
          </colgroup>
          <tbody>
            {filtered.map((row,ri) => (
              <tr key={ri} style={{borderBottom:'.5px solid var(--hair)'}}>
                <td style={{position:'sticky',left:0,background:'var(--surface)',zIndex:2,
                  textAlign:'center',padding:'4px 4px',width:W.streak,minWidth:W.streak,maxWidth:W.streak}}>
                  <Streak v={row.streak}/>
                </td>
                <td style={{position:'sticky',left:W.streak,background:'var(--surface)',zIndex:2,
                  padding:'4px 3px',width:W.trend,minWidth:W.trend,maxWidth:W.trend}}>
                  <Spark data={row.spark} w={48} h={20}/>
                </td>
                <td style={{position:'sticky',left:W.streak+W.trend,background:'var(--surface)',zIndex:2,
                  textAlign:'left',fontWeight:500,color:'var(--ink)',padding:'4px 6px',
                  width:W.name,minWidth:W.name,maxWidth:W.name,
                  overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',
                  cursor: onRowClick ? 'pointer' : 'default'}}
                  onClick={() => onRowClick && onRowClick(row.kiteSymbol ?? row.name)}>
                  <div style={{display:'flex',alignItems:'center',gap:4}}>
                    <Ticker name={row.name} sector={row.sector}/>
                    {onRemoveRow && (
                      <button
                        onClick={e => { e.stopPropagation(); onRemoveRow(row.name); }}
                        style={{marginLeft:'auto',flexShrink:0,background:'none',border:'none',
                          cursor:'pointer',color:'var(--ink-3)',fontSize:11,lineHeight:1,
                          padding:'0 2px',opacity:0.5}}
                        title="Remove from watchlist">
                        ✕
                      </button>
                    )}
                  </div>
                </td>
                {sectorFilter && (
                  <td style={{position:'sticky',left:leftSector,background:'var(--surface)',zIndex:2,
                    textAlign:'left',fontSize:11,color:'var(--ink-3)',padding:'4px 6px',
                    width:W.sector,minWidth:W.sector,maxWidth:W.sector,
                    overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}
                    title={row.sector}>
                    {row.sector}
                  </td>
                )}
                {view==='period' && shownPeriods.map(p=><HCell key={p} v={row[COL_KEY[p]]??0}/>)}
                {view==='period' && active.includes('custom') && custom && <HCell v={row.d10??0}/>}
                {view==='period' && showVsNifty && <HCell v={row.vsNifty??0}/>}
                {view==='daily' && Array.from({length:winSize},(_,i)=>(
                  <HCell key={i} v={row.daily?.[i]?.pct??0}/>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
}

// ── Sub-tab row — sticky ──────────────────────────────────────────────────────
// permKey maps each sub-tab to its permission key in the permission_config table
// Note: 'watchlists' and 'ai' are detached from nav (moved to H4 Strategies later).
// Their components (WatchlistsTab, MarketAISection) remain in file, untouched.
const TABS_DEF = [
  {v:'performance', l:'Performance Tables', permKey:'market_perf'},
  {v:'rs',          l:'Relative Strength',  permKey:'market_rs'},
  {v:'rotation',    l:'Sector Rotation',    permKey:'market_rotation'},
  {v:'drivers',     l:'Index Drivers',      permKey:'market_drivers'},
  {v:'events',      l:'Events & Actions',   permKey:'market_events'},
  {v:'directory',   l:'NSE Directory',      permKey:'market_directory'},
  {v:'analyst',     l:'Market AI',          permKey:'market_perf'},
];

function StickySubTabs({ val, onChange, onRefresh, refreshing, lastRefreshed }) {
  const { isAdmin, can } = useUser();
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 719px)').matches;

  return (
    <div style={{
      position:'sticky', top:TOP_SUBTAB, zIndex:50,
      background:'var(--paper)',
      borderBottom:'.5px solid var(--hair)',
      minHeight: H_SUBTAB,
      boxSizing:'border-box',
      display:'flex', flexDirection:'column', justifyContent:'center',
    }}>
      <div style={{display:'flex', alignItems:'center', gap:0}}>
        {/* overflowX:auto lets the tab row scroll on narrow screens without widening the page */}
        <div style={{display:'flex', overflowX:'auto', gap:0, flex:1,
          scrollbarWidth:'none', WebkitOverflowScrolling:'touch'}}>
          {TABS_DEF.map(t => {
            const locked = !isAdmin && !can(t.permKey);
            const active = t.v === val;
            return (
              <button key={t.v}
                disabled={locked}
                title={locked ? 'You don\'t have access to this section' : undefined}
                style={{
                  fontSize:11, fontWeight:active?700:500,
                  padding:'6px 14px',
                  cursor: locked ? 'default' : 'pointer',
                  color: locked ? 'var(--ink-3)' : active ? 'var(--ink)' : 'var(--ink-2)',
                  background:'none', border:'none',
                  borderBottom: active ? '2px solid var(--amber-ink)' : '2px solid transparent',
                  marginBottom:-1,
                  whiteSpace:'nowrap', transition:'all .15s',
                  opacity: locked ? 0.55 : 1,
                  display:'flex', alignItems:'center', gap:4,
                }}
                onClick={() => !locked && onChange(t.v)}>
                {t.l}
                {locked && <span style={{fontSize:11, lineHeight:1}}>🔒</span>}
              </button>
            );
          })}
        </div>
        {/* Refresh control — right side; timestamp hidden on mobile to save tab-bar space */}
        <div style={{display:'flex', alignItems:'center', gap:6,
          padding:'0 6px', flexShrink:0}}>
          {!isMobile && lastRefreshed && !refreshing && (
            <span style={{fontSize:11, color:'var(--ink-3)', whiteSpace:'nowrap'}}>
              Last updated {fmtRefreshTime(lastRefreshed)}
            </span>
          )}
          {refreshing && (
            <span style={{fontSize:11, color:'var(--ink-3)', whiteSpace:'nowrap'}}>
              {isMobile ? '⏳' : '⏳ Loading…'}
            </span>
          )}
          <button className="pill" onClick={onRefresh} disabled={refreshing}
            style={{padding:'3px 8px', fontSize:11, whiteSpace:'nowrap',
              display:'inline-flex', alignItems:'center', gap:4}}>
            <IconRefresh size={13}/>{!isMobile && ' Refresh'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Data transformation helpers ───────────────────────────────────────────────
// Converts the API `closes` array → the row shape PerfTable already expects.
// PerfTable itself is untouched — it still reads d1, d3, d5 etc.

function periodRet(closes, nDays) {
  if (!closes || closes.length < 2) return 0;
  const idx = closes.length - 1 - nDays;
  if (idx < 0) return 0;
  const base   = closes[idx]?.close;
  const recent = closes[closes.length - 1]?.close;
  if (!base) return 0;
  return +((recent - base) / base * 100).toFixed(2);
}

function calcStreak(closes) {
  if (!closes || closes.length < 2) return '—';
  let dir = null, count = 0;
  for (let i = closes.length - 1; i >= 1; i--) {
    const chg = closes[i].close - closes[i-1].close;
    const d = chg > 0 ? 1 : chg < 0 ? -1 : 0;
    if (d === 0) break;
    if (dir === null) { dir = d; count = 1; }
    else if (d === dir) count++;
    else break;
  }
  if (dir === null) return '—';
  return dir > 0 ? `↑${count}` : `↓${count}`;
}

function calcSpark(closes, nPoints = 20) {
  if (!closes || closes.length < 2) return [];
  const slice = closes.slice(-nPoints);
  const base = slice[0].close;
  if (!base) return [];
  return slice.map(c => +(c.close / base * 100).toFixed(2));
}

function calcDaily(closes, nDays = 30) {
  if (!closes || closes.length < 2) return [];
  const slice = closes.slice(-(nDays + 1));
  const result = [];
  for (let i = slice.length - 1; i >= 1; i--) {
    const curr = slice[i].close;
    const prev = slice[i-1].close;
    // Carry the actual trading date so column headers can show real dates
    result.push({
      pct:  prev !== 0 ? +((curr - prev) / prev * 100).toFixed(2) : 0,
      date: slice[i].date ?? null,   // e.g. "2026-04-30"
    });
  }
  return result; // index 0 = most recent trading day
}

function apiRowToTableRow(apiRow, niftyCloses) {
  const c = apiRow.closes ?? [];
  const stockRet30 = periodRet(c, 30);
  const niftyRet30 = periodRet(niftyCloses, 30);
  return {
    name:       apiRow.label,
    sector:     apiRow.sector,
    kiteSymbol: apiRow.symbol,  // passed to SidePanel on row click
    streak:  calcStreak(c),
    spark:   calcSpark(c),
    daily:   calcDaily(c, 30),
    d1:      apiRow.chg_pct != null ? apiRow.chg_pct : periodRet(c, 1),
    d3:      periodRet(c, 3),
    d5:      periodRet(c, 5),
    d10:     periodRet(c, 10),
    d15:     periodRet(c, 15),
    d30:     stockRet30,
    d90:     periodRet(c, 90),
    vsNifty: +(stockRet30 - niftyRet30).toFixed(2),
  };
}

// ── Module-level in-flight guard — prevents duplicate concurrent fetches ─────
const _inflight = {};

// ── usePerfData hook ──────────────────────────────────────────────────────────
function usePerfData(universe) {
  const cached = _cacheGet(universe);
  const [rows,         setRows]         = useState(cached ?? []);
  const [loading,      setLoading]      = useState(!cached);
  const [error,        setError]        = useState(null);
  const [lastRefreshed,setLastRefreshed]= useState(cached ? new Date(_perfCache[universe].ts) : null);

  const fetch_ = useCallback(async (force = false) => {
    if (!force) {
      const hit = _cacheGet(universe);
      if (hit) {
        setRows(hit);
        setLastRefreshed(new Date(_perfCache[universe].ts));
        setLoading(false);
        return;
      }
    }
    // If a fetch is already in-flight for this universe, wait for it
    if (_inflight[universe]) {
      try {
        const data = await _inflight[universe];
        setRows(data);
        setLastRefreshed(new Date(_perfCache[universe].ts));
      } catch(e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
      return;
    }
    setLoading(true); setError(null);
    const promise = getPerformanceTable(universe, force)
      .then(res => {
        const data = res.rows ?? [];
        _cacheSet(universe, data);
        return data;
      })
      .finally(() => { delete _inflight[universe]; });
    _inflight[universe] = promise;
    try {
      const data = await promise;
      setRows(data);
      setLastRefreshed(new Date());
    } catch(e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [universe]);

  useEffect(() => {
    const cached = _cacheGet(universe);
    if (cached) {
      setRows(cached);
      setLastRefreshed(new Date(_perfCache[universe].ts));
      setLoading(false);
    } else {
      fetch_();
    }
  }, [fetch_, universe]);

  return { rows, loading, error, lastRefreshed, refetch: () => fetch_(true) };
}

// ── Format last refreshed time ────────────────────────────────────────────────
function fmtRefreshTime(d) {
  if (!d) return null;
  // Format: "15 Apr, 02:54 am IST"
  const ist = new Date(d.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const day  = ist.getDate();
  const mon  = ist.toLocaleString('en-GB', { month: 'short' });
  const h24  = ist.getHours();
  const min  = String(ist.getMinutes()).padStart(2, '0');
  const ampm = h24 >= 12 ? 'pm' : 'am';
  const h12  = h24 % 12 || 12;
  const hStr = String(h12).padStart(2, '0');
  return `${day} ${mon}, ${hStr}:${min} ${ampm} IST`;
}

// ── Status helpers ────────────────────────────────────────────────────────────

function ErrorBanner({ message, onRetry }) {
  return (
    <div style={{padding:'10px 14px', borderRadius:8,
      background:'var(--down-field-bg)', color:'var(--down-field-ink)', fontSize:11, marginBottom:12,
      display:'flex', alignItems:'center', justifyContent:'space-between', gap:8}}>
      <span>
        {message}
        {message?.includes('503') && (
          <> — <a href={`${process.env.REACT_APP_API_URL || "http://localhost:8000"}/kite/login`} target="_blank" rel="noreferrer"
            style={{color:'var(--amber-ink)'}}>Log in to Kite</a></>
        )}
      </span>
      <button className="pill" onClick={onRetry} style={{flexShrink:0}}>Retry</button>
    </div>
  );
}

// ── Collapsible section header — same triangle style as RelativeStrengthTab ──
function SecHdr({ open, onToggle, children, count }) {
  return (
    <div style={{ margin:'14px 0 4px', display:'flex', alignItems:'center', gap:6 }}>
      <button onClick={onToggle}
        style={{ display:'flex', alignItems:'center', gap:5, background:'none', border:'none',
          cursor:'pointer', padding:0, color:'var(--ink-3)' }}>
        <span style={{ fontSize:11, fontWeight:700, letterSpacing:'.07em', textTransform:'uppercase' }}>
          {children}
          {count != null && <span style={{ fontWeight:400, marginLeft:4 }}>({count})</span>}
        </span>
        <span style={{ fontSize:16, lineHeight:1, transition:'transform .2s',
          display:'inline-block', transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
          color:'var(--ink-2)' }}>▾</span>
      </button>
    </div>
  );
}

// ── Indices & Sectors — derived from INDEX_REGISTRY (single source of truth) ──
// BROAD: Index category + Broad category + any entry with perfBroad:true (e.g. Bank Nifty)
// SECTOR: Sector + Thematic categories, excluding perfBroad entries
// displayLabel uses KITE_TO_DISPLAY[apiRow.symbol] — no hardcoded LABEL_MAP needed.

const BROAD_SET = new Set(
  INDEX_REGISTRY
    .filter(e => e.category === 'Broad' || e.category === 'Index' || e.perfBroad)
    .map(e => e.sectorName || e.label)
);
const SECTOR_SET = new Set(
  INDEX_REGISTRY
    .filter(e => (e.category === 'Sector' || e.category === 'Thematic') && !e.perfBroad)
    .map(e => e.sectorName || e.label)
);

function IndicesTab({ indicesData, n100Data, niftyCloses, assetsData }) {
  const { open: openPanel } = useSidePanel();

  // Collapsible state — all open by default
  const [assetsOpen,  setAssetsOpen]  = useState(true);
  const [broadOpen,   setBroadOpen]   = useState(true);
  const [sectorsOpen, setSectorsOpen] = useState(true);
  const [n100Open,    setN100Open]    = useState(true);

  const allIndexRows = useMemo(() => {
    return indicesData.rows.map(apiRow => {
      // Use canonical display name from indices.js (kiteSymbol → sectorName || label).
      // Falls back to raw API label if the symbol isn't in the registry.
      const displayLabel = KITE_TO_DISPLAY[apiRow.symbol] ?? apiRow.label;
      return apiRowToTableRow({ ...apiRow, label: displayLabel }, niftyCloses);
    });
  }, [indicesData.rows, niftyCloses]);

  const n100Rows = useMemo(() => {
    return n100Data.rows.map(apiRow => apiRowToTableRow(apiRow, niftyCloses));
  }, [n100Data.rows, niftyCloses]);

  const assetsRows = useMemo(() => {
    return (assetsData?.rows ?? []).map(apiRow => apiRowToTableRow(apiRow, niftyCloses));
  }, [assetsData?.rows, niftyCloses]);

  const broad  = allIndexRows.filter(r => BROAD_SET.has(r.name));
  const sector = allIndexRows.filter(r => SECTOR_SET.has(r.name));
  const bRows  = broad.length  ? broad  : allIndexRows.slice(0,10);
  const sRows  = sector.length ? sector : allIndexRows.slice(10,26);

  const error = indicesData.error || n100Data.error || assetsData?.error;

  return (
    <div>
      {error && <ErrorBanner message={error} onRetry={()=>{indicesData.refetch();n100Data.refetch();assetsData?.refetch?.();}}/>}

      {/* When open: PerfTable renders with sectionTitle+toggle inside its own sticky block.
          When closed: SecHdr shows a collapsed one-liner with the expand triangle. */}

      {!assetsOpen  && <SecHdr open={false} onToggle={() => setAssetsOpen(true)}  count={assetsRows.length}>Assets</SecHdr>}
      {assetsOpen   && <PerfTable rows={assetsRows} showVsNifty={false} sectorFilter={false} headerTop={TOP_TABLE}
          sectionTitle="Assets" onToggle={() => setAssetsOpen(false)}
          onRowClick={sym => openPanel({ name: sym })}/>}

      {!broadOpen   && <SecHdr open={false} onToggle={() => setBroadOpen(true)}   count={bRows.length}>Broad Market Indices</SecHdr>}
      {broadOpen    && <PerfTable rows={bRows} showVsNifty={false} sectorFilter={false} headerTop={TOP_TABLE}
          sectionTitle="Broad Market Indices" onToggle={() => setBroadOpen(false)}
          onRowClick={sym => openPanel({ name: sym })}/>}

      {!sectorsOpen && <SecHdr open={false} onToggle={() => setSectorsOpen(true)} count={sRows.length}>Sector Indices</SecHdr>}
      {sectorsOpen  && <PerfTable rows={sRows} showVsNifty={false} sectorFilter={false} headerTop={TOP_TABLE}
          sectionTitle="Sector Indices" onToggle={() => setSectorsOpen(false)}
          onRowClick={sym => openPanel({ name: sym })}/>}

      {!n100Open    && <SecHdr open={false} onToggle={() => setN100Open(true)}    count={n100Rows.length}>NIFTY 100 Stocks</SecHdr>}
      {n100Open     && <PerfTable rows={n100Rows} showVsNifty={true} sectorFilter={true} headerTop={TOP_TABLE}
          sectionTitle="NIFTY 100 Stocks" onToggle={() => setN100Open(false)}
          onRowClick={sym => openPanel({ name: sym })}/>}
    </div>
  );
}

function FOTab({ foData, niftyCloses }) {
  const { open: openPanel } = useSidePanel();

  const foRows = useMemo(() => {
    return foData.rows.map(apiRow => apiRowToTableRow(apiRow, niftyCloses));
  }, [foData.rows, niftyCloses]);

  return (
    <div>
      {foData.error && <ErrorBanner message={foData.error} onRetry={foData.refetch}/>}
      <PerfTable rows={foRows} showVsNifty={true} sectorFilter={true}
        sectionTitle="F&O Eligible Stocks" headerTop={TOP_TABLE}
        onRowClick={sym => openPanel({ name: sym })}/>
    </div>
  );
}

// ── Symbol autocomplete input ─────────────────────────────────────────────────
function _fuzzyScore(haystack, needle) {
  if (!haystack || !needle) return 0;
  if (haystack === needle) return 3;
  if (haystack.startsWith(needle)) return 2;
  if (haystack.includes(needle)) return 1;
  let hi = 0;
  for (let ni = 0; ni < needle.length; ni++) {
    const pos = haystack.indexOf(needle[ni], hi);
    if (pos === -1) return 0;
    hi = pos + 1;
  }
  return 0.5;
}

function SymbolAutocomplete({ value, onChange, onSelect, universe, placeholder='Add symbol...' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const matches = useMemo(() => {
    if (!value || value.length < 1) return [];
    const q = value.toUpperCase();
    return universe
      .map(u => ({ u, score: Math.max(_fuzzyScore(u.symbol, q), _fuzzyScore(u.label.toUpperCase(), q)) }))
      .filter(x => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .map(x => x.u)
      .slice(0, 8);
  }, [value, universe]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{position:'relative'}}>
      <input
        className="inp"
        placeholder={placeholder}
        value={value}
        onChange={e => { onChange(e.target.value.toUpperCase()); setOpen(true); }}
        onFocus={() => value && setOpen(true)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            if (matches.length > 0) { onSelect(matches[0].symbol); setOpen(false); }
            else { onSelect(value); setOpen(false); }
          }
          if (e.key === 'Escape') setOpen(false);
        }}
        style={{width:130}}
        autoComplete="off"
      />
      {open && matches.length > 0 && (
        <div style={{
          position:'absolute', top:'100%', left:0, zIndex:100,
          background:'var(--surface)', border:'.5px solid var(--hair)',
          borderRadius:6, boxShadow:'0 4px 16px rgba(0,0,0,.15)',
          minWidth:220, maxHeight:240, overflowY:'auto',
          marginTop:2,
        }}>
          {matches.map(u => (
            <div key={u.symbol}
              onMouseDown={e => { e.preventDefault(); onSelect(u.symbol); setOpen(false); }}
              style={{
                padding:'6px 10px', cursor:'pointer',
                display:'flex', gap:8, alignItems:'center',
                borderBottom:'.5px solid var(--hair)',
              }}
              onMouseEnter={e => e.currentTarget.style.background='var(--surface-2)'}
              onMouseLeave={e => e.currentTarget.style.background='transparent'}
            >
              <span style={{fontWeight:600, color:'var(--ink)', fontSize:11, minWidth:90,
                fontFamily:'var(--font-ui)'}}>
                {u.symbol}
              </span>
              {u.label && u.label !== u.symbol && (
                <span style={{color:'var(--ink-3)', fontSize:11, overflow:'hidden',
                  textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:'var(--font-ui)'}}>
                  {u.label}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Watchlists — API-persisted, real performance data ────────────────────────
export function WatchlistsTab({ foData: foDataProp, niftyCloses: niftyClosesProp, symbolUniverse: symbolUniverseProp, indicesData: indicesDataProp, n100Data: n100DataProp }) {
  const [lists,         setLists]       = useState([]);
  const [addTxt,        setAddTxt]      = useState({});
  const [wlError,       setWlError]     = useState(null);
  const [editingId,     setEditingId]   = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, name }
  const wlErrorTimerRef = useRef(null);

  // Auto-dismiss error after 10s
  useEffect(() => {
    if (!wlError) return;
    clearTimeout(wlErrorTimerRef.current);
    wlErrorTimerRef.current = setTimeout(() => setWlError(null), 10000);
    return () => clearTimeout(wlErrorTimerRef.current);
  }, [wlError]);

  // When props are empty (used standalone in Strategies tab), self-fetch data
  const selfIndices = usePerfData('indices');
  const selfN100    = usePerfData('nifty100');
  const selfFo      = usePerfData('fo');

  const isEmpty = (d) => !d || !d.rows || d.rows.length === 0;
  const indicesData  = isEmpty(indicesDataProp) ? selfIndices : indicesDataProp;
  const n100Data     = isEmpty(n100DataProp)    ? selfN100    : n100DataProp;
  const foData       = isEmpty(foDataProp)      ? selfFo      : foDataProp;

  const niftyCloses = useMemo(() => {
    if (niftyClosesProp && niftyClosesProp.length > 0) return niftyClosesProp;
    const r = indicesData.rows.find(r => r.symbol === 'NIFTY 50');
    return r?.closes ?? [];
  }, [niftyClosesProp, indicesData.rows]);

  const symbolUniverse = useMemo(() => {
    if (symbolUniverseProp && symbolUniverseProp.length > 0) return symbolUniverseProp;
    const seen = new Set();
    const list = [];
    [...indicesData.rows, ...n100Data.rows, ...foData.rows].forEach(r => {
      if (r.symbol && !seen.has(r.symbol)) {
        seen.add(r.symbol);
        list.push({ symbol: r.symbol, label: r.label || r.symbol });
      }
    });
    return list;
  }, [symbolUniverseProp, indicesData.rows, n100Data.rows, foData.rows]);

  // Build lookup map from ALL universes: symbol → table row
  const foRowMap = useMemo(() => {
    const map = {};
    const addApiRows = (apiRows) => {
      apiRows.forEach(apiRow => {
        const row = apiRowToTableRow(apiRow, niftyCloses);
        map[apiRow.symbol] = row;
      });
    };
    addApiRows(indicesData.rows);
    addApiRows(n100Data.rows);
    addApiRows(foData.rows);
    return map;
  }, [foData.rows, indicesData.rows, n100Data.rows, niftyCloses]);

  // Resolve a symbol to a table row — use fo data if available, else minimal placeholder
  const resolveRow = useCallback((symbol) => {
    if (foRowMap[symbol]) return foRowMap[symbol];
    // Fallback for symbols not in fo universe (e.g. NIFTY 100 only stocks)
    return {
      name: symbol, sector: 'Other', streak: '—',
      spark: [], daily: [],
      d1:0, d3:0, d5:0, d10:0, d15:0, d30:0, d90:0, vsNifty:0,
    };
  }, [foRowMap]);

  // noteEdits: { [ticker]: currentDraftText } — only set while editing
  const [noteEdits, setNoteEdits] = useState({});
  // noteSaving: Set of tickers whose note is being saved
  const [noteSaving, setNoteSaving] = useState(new Set());

  // Load watchlists on mount
  useEffect(() => {
    fetch(`${API}/api/watchlists`)
      .then(r => r.json())
      .then(data => setLists(data.watchlists ?? []))
      .catch(() => setWlError('Could not load watchlists'));
  }, []);

  const addList = async () => {
    try {
      const res  = await fetch(`${API}/api/watchlists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: 'New Watchlist' }),
      });
      const wl = await res.json();
      setLists(l => [...l, wl]);
    } catch { setWlError('Could not create watchlist'); }
  };

  const addStock = async (wid, symOverride) => {
    const sym = (symOverride || addTxt[wid] || '').trim().toUpperCase();
    if (!sym) return;
    // Client-side fast pre-check (UX only — backend is authoritative)
    if (symbolUniverse.length > 0) {
      const valid = symbolUniverse.some(u => (u.symbol || u) === sym);
      if (!valid) { setWlError(`"${sym}" not found in symbol universe — check the ticker and try again`); return; }
    }
    setWlError(null);
    try {
      const res = await fetch(`${API}/api/watchlists/${wid}/entries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticker: sym }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setWlError(err.detail || `Could not add "${sym}"`);
        return;
      }
      const updated = await res.json();
      setLists(l => l.map(x => x.id === wid ? updated : x));
      setAddTxt(t => ({ ...t, [wid]: '' }));
    } catch { setWlError('Could not add stock'); }
  };

  const removeStock = async (wid, sym) => {
    try {
      const res = await fetch(`${API}/api/watchlists/${wid}/entries/${encodeURIComponent(sym)}`, {
        method: 'DELETE',
      });
      if (!res.ok) { setWlError('Could not remove stock'); return; }
      const updated = await res.json();
      setLists(l => l.map(x => x.id === wid ? updated : x));
    } catch { setWlError('Could not remove stock'); }
  };

  const renameList = async (wid, name) => {
    try {
      const res = await fetch(`${API}/api/watchlists/${wid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const updated = await res.json();
      setLists(l => l.map(x => x.id === wid ? updated : x));
    } catch { setWlError('Could not rename watchlist'); }
  };

  const delList = (wid, name) => setConfirmDelete({ id: wid, name });

  const confirmDelList = async () => {
    if (!confirmDelete) return;
    const { id } = confirmDelete;
    setConfirmDelete(null);
    try {
      await fetch(`${API}/api/watchlists/${id}`, { method: 'DELETE' });
      setLists(l => l.filter(x => x.id !== id));
    } catch { setWlError('Could not delete watchlist'); }
  };

  // Save global ticker note. When saved, propagate the new note to ALL watchlists
  // that contain this ticker so every list reflects the update immediately.
  const saveNote = async (ticker) => {
    const note = (noteEdits[ticker] ?? '').trim();
    setNoteSaving(s => new Set([...s, ticker]));
    try {
      const res = await fetch(`${API}/api/ticker-notes/${encodeURIComponent(ticker)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note }),
      });
      if (!res.ok) { setWlError('Could not save note'); return; }
      const saved = await res.json();
      // Propagate to all watchlists in local state
      setLists(ls => ls.map(wl => ({
        ...wl,
        entries: (wl.entries || []).map(e =>
          e.ticker === ticker ? { ...e, note: saved.note } : e
        ),
      })));
      setNoteEdits(m => { const n = { ...m }; delete n[ticker]; return n; });
    } catch { setWlError('Could not save note'); }
    finally { setNoteSaving(s => { const n = new Set(s); n.delete(ticker); return n; }); }
  };

  return (
    <div>
      {wlError && (
        <div style={{marginBottom:8,padding:'7px 12px',background:'rgba(136,30,16,.08)',
          border:'.5px solid rgba(136,30,16,.3)',borderRadius:6,fontSize:11,color:'var(--down-field-ink)',
          display:'flex',alignItems:'center',justifyContent:'space-between',gap:8}}>
          <span>{wlError}</span>
          <button onClick={()=>setWlError(null)}
            style={{background:'none',border:'none',cursor:'pointer',fontSize:14,lineHeight:1,
              color:'var(--down-field-ink)',padding:'0 2px',flexShrink:0}}>×</button>
        </div>
      )}

      {/* Jump-to bar — minHeight:32 must match TOP_WL_HDR - TOP_JUMPBAR to prevent leak-through */}
      <div style={{
        position:'sticky', top:TOP_JUMPBAR, zIndex:22,
        background:'var(--paper)',
        padding:'6px 0', borderBottom:'.5px solid var(--hair)',
        minHeight:32,
        display:'flex', gap:6, flexWrap:'wrap', alignItems:'center',
      }}>
        <span style={{fontSize:11,color:'var(--ink-3)'}}>Jump to:</span>
        {lists.map(wl=>(
          <button key={wl.id}
            onClick={() => {
              const el = document.getElementById(`wl-${wl.id}`);
              if (!el) return;
              const offset = TOP_WL_HDR + 8; // sticky headers height + small gap
              const top = el.getBoundingClientRect().top + window.scrollY - offset;
              window.scrollTo({ top, behavior: 'smooth' });
            }}
            style={{fontSize:11,fontWeight:500,color:'var(--amber-ink)',
              padding:'2px 8px',border:'.5px solid var(--hair)',borderRadius:3,
              background:'none',cursor:'pointer'}}>
            {wl.name}
          </button>
        ))}
        <button onClick={addList}
          style={{fontSize:11,fontWeight:600,padding:'3px 12px',borderRadius:4,border:'none',
            color:'#fff',background:'var(--amber-ink)',cursor:'pointer',marginLeft:'auto',
            whiteSpace:'nowrap'}}>
          + New Watchlist
        </button>
      </div>

      {/* Delete confirm modal */}
      {confirmDelete && (
        <div style={{
          position:'fixed', inset:0, zIndex:9000,
          background:'rgba(0,0,0,.35)',
          display:'flex', alignItems:'center', justifyContent:'center',
        }} onClick={()=>setConfirmDelete(null)}>
          <div style={{
            background:'var(--paper)', border:'.5px solid var(--hair)',
            borderRadius:10, padding:'24px 28px', maxWidth:340, width:'90%',
            boxShadow:'0 8px 32px rgba(0,0,0,.18)',
          }} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:13,fontWeight:700,color:'var(--ink)',marginBottom:8}}>
              Delete watchlist?
            </div>
            <div style={{fontSize:12,color:'var(--ink-2)',marginBottom:20,lineHeight:1.5}}>
              "<strong>{confirmDelete.name}</strong>" will be permanently deleted. This cannot be undone.
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button className="pill" onClick={()=>setConfirmDelete(null)}
                style={{padding:'5px 16px',fontSize:12}}>
                Cancel
              </button>
              <button onClick={confirmDelList}
                style={{padding:'5px 16px',fontSize:12,fontWeight:600,borderRadius:4,border:'none',
                  cursor:'pointer',background:'var(--down-field-ink)',color:'#fff'}}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {lists.length === 0 && (
        <div style={{padding:'24px',textAlign:'center',color:'var(--ink-3)',fontSize:11}}>
          No watchlists yet. Click "+ New Watchlist" to create one.
        </div>
      )}

      {lists.map(wl => {
        const entries  = wl.entries || wl.symbols.map(s => ({ ticker: s, note: null }));
        const wlRows   = entries.map(e => resolveRow(e.ticker));
        const isEditing = editingId === wl.id;
        return (
          <div key={wl.id} id={`wl-${wl.id}`} style={{marginTop:20}}>
            <PerfTable rows={wlRows} showVsNifty={true} sectorFilter={false}
              headerTop={TOP_WL_HDR} headerZIndex={18}
              onRemoveRow={sym => removeStock(wl.id, sym)}
              stickyTitle={
                <div style={{
                  display:'flex', alignItems:'center', gap:6,
                  padding:'5px 2px 3px',
                  borderBottom:'.5px solid var(--hair)',
                }}>
                  {isEditing ? (
                    <input autoFocus value={wl.name} className="inp"
                      onChange={e => setLists(l => l.map(x => x.id===wl.id ? {...x, name:e.target.value} : x))}
                      onBlur={e => { renameList(wl.id, e.target.value); setEditingId(null); }}
                      onKeyDown={e => { if (e.key==='Enter') { renameList(wl.id, e.target.value); setEditingId(null); } if (e.key==='Escape') setEditingId(null); }}
                      style={{fontSize:12,fontWeight:700,background:'var(--ibg)',border:'.5px solid var(--amber-ink)',
                        borderRadius:3,padding:'2px 6px',outline:'none',color:'var(--ink)',minWidth:100}}/>
                  ) : (
                    <span style={{fontSize:12,fontWeight:700,color:'var(--ink)'}}>{wl.name}</span>
                  )}
                  <span style={{fontSize:11,color:'var(--ink-3)'}}>({entries.length} stocks)</span>
                  <button onClick={()=>setEditingId(isEditing ? null : wl.id)} title="Rename watchlist"
                    style={{background:'none',border:'none',cursor:'pointer',fontSize:13,lineHeight:1,
                      color:'var(--ink-3)',padding:'1px 3px',display:'flex',alignItems:'center'}}>✎</button>
                  <button onClick={()=>delList(wl.id,wl.name)} title="Delete watchlist"
                    style={{background:'none',border:'none',cursor:'pointer',fontSize:14,lineHeight:1,
                      color:'var(--ink-3)',padding:'1px 3px',display:'flex',alignItems:'center'}}>✕</button>
                </div>
              }
              extraControls={
                <div style={{display:'flex',alignItems:'center',gap:4}}>
                  <SymbolAutocomplete
                    value={addTxt[wl.id]||''}
                    onChange={val => setAddTxt(t=>({...t,[wl.id]:val}))}
                    onSelect={sym => { setAddTxt(t=>({...t,[wl.id]:sym})); addStock(wl.id, sym); }}
                    universe={symbolUniverse}
                  />
                  <button className="pill on"
                    onClick={()=>addStock(wl.id)}
                    onKeyDown={e=>e.key==='Enter'&&addStock(wl.id)}>
                    Add
                  </button>
                </div>
              }
              rowExtras={sym => {
                const entry = entries.find(e => e.ticker === sym);
                if (!entry) return null;
                const isEditingNote = sym in noteEdits;
                const draft = noteEdits[sym] ?? '';
                const currentNote = entry.note;
                return (
                  <div style={{fontSize:11,color:'var(--ink-3)',marginTop:2,paddingLeft:2}}>
                    {isEditingNote ? (
                      <div style={{display:'flex',gap:4,alignItems:'center'}}>
                        <input
                          autoFocus
                          value={draft}
                          onChange={e => setNoteEdits(m => ({...m,[sym]:e.target.value}))}
                          onKeyDown={e => {
                            if (e.key==='Enter') saveNote(sym);
                            if (e.key==='Escape') setNoteEdits(m => { const n={...m}; delete n[sym]; return n; });
                          }}
                          placeholder="Add a note (Enter to save, Esc to cancel)…"
                          style={{fontSize:11,padding:'2px 6px',border:'.5px solid var(--hair-strong)',
                            borderRadius:3,background:'var(--ibg)',color:'var(--ink)',outline:'none',
                            flexGrow:1,minWidth:0}}
                        />
                        <button onClick={()=>saveNote(sym)} disabled={noteSaving.has(sym)}
                          style={{fontSize:10,padding:'1px 6px',borderRadius:3,border:'none',
                            background:'var(--amber-ink)',color:'#fff',cursor:'pointer',whiteSpace:'nowrap'}}>
                          {noteSaving.has(sym) ? '…' : 'Save'}
                        </button>
                        <button onClick={()=>setNoteEdits(m=>{const n={...m};delete n[sym];return n;})}
                          style={{fontSize:10,padding:'1px 4px',borderRadius:3,border:'.5px solid var(--hair)',
                            background:'none',color:'var(--ink-3)',cursor:'pointer'}}>
                          ✕
                        </button>
                      </div>
                    ) : currentNote ? (
                      <span
                        title="Click to edit note"
                        onClick={() => setNoteEdits(m => ({...m,[sym]:currentNote}))}
                        style={{cursor:'pointer',fontStyle:'italic',borderBottom:'.5px dashed var(--hair-strong)'}}>
                        {currentNote}
                      </span>
                    ) : (
                      <span
                        onClick={() => setNoteEdits(m => ({...m,[sym]:''}))}
                        style={{cursor:'pointer',opacity:0.5}}>
                        + note
                      </span>
                    )}
                  </div>
                );
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

// ── F&O Watchlist — live data from /api/ai/reports/market ────────────────────
const CONV_STYLE = {
  High:   { bg:'rgba(26,106,26,.12)',  c:'#1a6a1a', border:'rgba(26,106,26,.3)'  },
  Medium: { bg:'rgba(138,90,0,.10)',   c:'#8a5a00', border:'rgba(138,90,0,.25)'  },
};

// ── Market AI — collapsible day cards, RS-based F&O watchlist ────────────────
// ── MarketAnalystSection — MA1 Stage 3 surface ───────────────────────────────
// Reuses the day-card + generate-button pattern from MarketAISection below.
// Schema: headline, whats_building[], whats_turning[], index_structure,
//         rotation, breadth_note, on_the_radar[], + provenance footer.

// yf-derived token (^prefix stripped) → kiteSymbol that openPanel understands.
// The analyst emits these tokens in prose; they must be resolved before the panel call.
const YF_TOKEN_TO_KITE = {
  NSEI:       'NIFTY 50',
  BSESN:      'SENSEX',
  INDIAVIX:   'INDIA VIX',
  NSEBANK:    'NIFTY BANK',
  CNXIT:      'NIFTY IT',
  CNXPHARMA:  'NIFTY PHARMA',
  CNXFMCG:    'NIFTY FMCG',
  CNXMETAL:   'NIFTY METAL',
  CNXAUTO:    'NIFTY AUTO',
  CNXREALTY:  'NIFTY REALTY',
  CNXENERGY:  'NIFTY ENERGY',
  CNXINFRA:   'NIFTY INFRA',
  CNXPSUBANK: 'NIFTY PSU BANK',
  CNXMEDIA:   'NIFTY MEDIA',
  CNXCONSUM:  'NIFTY CONSR DURBL',
  CNXCMDT:    'NIFTY COMMODITIES',
  CNXPSE:     'NIFTY PSE',
  CNXMNC:     'NIFTY MNC',
  CNXSERVICE: 'NIFTY SERV SECTOR',
};

// Words that look like tickers but should NOT open the side panel.
// All-caps abbreviations the analyst commonly uses in prose.
const ANALYST_NON_TICKERS = new Set([
  'A','D','IST','UTC','NFP','GDP','RBI','FII','DII','PCR','VIX','OI','RS','RRG',
  'NSE','BSE','ETF','IPO','FPO','QoQ','YoY','MoM','CEO','CFO','MD','PB','PE',
  'EPS','ROE','EBITDA','CAGR','T','F','B','US','UK','EU','RBI','SEBI','NSDL',
  'CDSL','EMA','SMA','WMA','MA','NIFTY','NIFTY50','SENSEX','BANKNIFTY',
  'HIGH','MEDIUM','LOW','AND','OR','FOR','THE','NOT','BUT','ALL','NEW',
]);

// Regex: 2-15 uppercase letters (plus optional & for names like M&M), word-boundary anchored.
// Strips trailing punctuation before testing.
const TICKER_RE = /\b([A-Z][A-Z0-9&]{1,14})\b/g;

function AnalystDayCard({ day, expanded, onToggle }) {
  const rj = day._rj || {};
  const { open: openPanel } = useSidePanel();

  const isUnavailable = (s) => s && (
    s.toUpperCase().includes('UNAVAILABLE') || s.toUpperCase().includes('STALE')
  );

  // Splits a plain text string into an array of React nodes, wrapping recognised
  // NSE ticker tokens with a clickable blue span that opens the side panel.
  const TickerText = ({ text }) => {
    if (!text) return null;
    const parts = [];
    let last = 0;
    let m;
    TICKER_RE.lastIndex = 0;
    while ((m = TICKER_RE.exec(text)) !== null) {
      const token = m[1];
      if (ANALYST_NON_TICKERS.has(token) || token.length < 2) continue;
      // Resolve yf-derived index tokens (e.g. CNXMETAL → NIFTY METAL) before panel call.
      const panelName = YF_TOKEN_TO_KITE[token] || token;
      if (m.index > last) parts.push(text.slice(last, m.index));
      parts.push(
        <span
          key={m.index}
          onClick={(e) => { e.stopPropagation(); openPanel({ name: panelName }); }}
          style={{
            color: 'var(--accent-blue)',
            fontWeight: 600,
            cursor: 'pointer',
            textDecoration: 'underline dotted var(--accent-blue)',
            textUnderlineOffset: 2,
          }}>
          {token}
        </span>
      );
      last = m.index + token.length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return <>{parts}</>;
  };

  const SectionLabel = ({ children }) => (
    <div style={{fontSize:10,fontWeight:700,letterSpacing:'.07em',textTransform:'uppercase',
      color:'var(--ink-3)',marginBottom:4,marginTop:10}}>
      {children}
    </div>
  );

  const BulletList = ({ items, emptyText }) => {
    if (!items || items.length === 0) {
      return emptyText
        ? <div style={{fontSize:11,color:'var(--ink-3)',fontStyle:'italic'}}>{emptyText}</div>
        : null;
    }
    return (
      <div style={{display:'flex',flexDirection:'column',gap:4}}>
        {items.map((item, i) => (
          <div key={i} style={{display:'flex',gap:6,alignItems:'flex-start'}}>
            <span style={{fontSize:11,color:'var(--ink-3)',flexShrink:0,marginTop:1}}>•</span>
            <span style={{fontSize:11,color:'var(--ink)',lineHeight:1.55}}>
              <TickerText text={item} />
            </span>
          </div>
        ))}
      </div>
    );
  };

  const ProseBlock = ({ text }) => {
    if (!text) return null;
    const warn = isUnavailable(text);
    return (
      <div style={{
        fontSize:11,
        color: warn ? 'var(--amber-ink)' : 'var(--ink-2)',
        lineHeight:1.55,
        background: warn ? 'rgba(245,158,11,.08)' : 'transparent',
        borderRadius: warn ? 3 : 0,
        padding: warn ? '4px 6px' : 0,
      }}>
        {warn ? text : <TickerText text={text} />}
      </div>
    );
  };

  return (
    <div className="card" style={{marginBottom:8,overflow:'hidden'}}>
      {/* Card header — date + headline, always visible */}
      <div
        style={{display:'flex',alignItems:'center',justifyContent:'space-between',
          padding:'8px 12px',background:'var(--surface-2)',cursor:'pointer',userSelect:'none'}}
        onClick={onToggle}>
        <div style={{display:'flex',alignItems:'center',gap:10,minWidth:0}}>
          <span style={{fontSize:12,fontWeight:700,color:'var(--ink)',flexShrink:0}}>{fmtDate(day.date)}</span>
          {rj.headline && (
            <span style={{fontSize:11,color:'var(--ink-2)',overflow:'hidden',textOverflow:'ellipsis',
              whiteSpace:'nowrap',maxWidth:520}}>
              {rj.headline}
            </span>
          )}
        </div>
        <span style={{fontSize:11,color:'var(--ink-3)',flexShrink:0,marginLeft:8,
          transform:expanded?'rotate(180deg)':'none',transition:'transform .2s'}}>▼</span>
      </div>

      {expanded && (
        <div style={{padding:'10px 14px 14px'}}>
          {rj.whats_building?.length > 0 && (
            <>
              <SectionLabel>What's Building</SectionLabel>
              <BulletList items={rj.whats_building} />
            </>
          )}

          <SectionLabel>What's Turning</SectionLabel>
          <BulletList items={rj.whats_turning}
            emptyText="Nothing notable turning across horizons." />

          {rj.index_structure && (
            <>
              <SectionLabel>Index Structure</SectionLabel>
              <ProseBlock text={rj.index_structure} />
            </>
          )}

          {rj.rotation && (
            <>
              <SectionLabel>Rotation</SectionLabel>
              <ProseBlock text={rj.rotation} />
            </>
          )}

          {rj.breadth_note && (
            <>
              <SectionLabel>Breadth</SectionLabel>
              <ProseBlock text={rj.breadth_note} />
            </>
          )}

          {rj.on_the_radar?.length > 0 && (
            <>
              <SectionLabel>On the Radar</SectionLabel>
              <BulletList items={rj.on_the_radar} />
            </>
          )}

          {/* Provenance footer */}
          <div style={{marginTop:12,paddingTop:8,borderTop:'.5px solid var(--hair)',
            display:'flex',flexWrap:'wrap',gap:12,alignItems:'center'}}>
            {rj.model && <span style={{fontSize:10,color:'var(--ink-3)'}}>model: {rj.model}</span>}
            {rj.prompt_version && <span style={{fontSize:10,color:'var(--ink-3)'}}>v{rj.prompt_version}</span>}
            {rj.data_as_of && <span style={{fontSize:10,color:'var(--ink-3)'}}>data as of {rj.data_as_of}</span>}
            {day._meta?.created_at && (
              <span style={{fontSize:10,color:'var(--ink-3)'}}>
                generated {(() => {
                  try {
                    const iso = day._meta.created_at.replace(' ','T')+'Z';
                    return new Date(iso).toLocaleTimeString('en-IN',
                      {timeZone:'Asia/Kolkata',hour:'2-digit',minute:'2-digit',hour12:false})+' IST';
                  } catch { return day._meta.created_at; }
                })()}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MarketAnalystSection() {
  const [days,       setDays]       = useState([]);
  const [status,     setStatus]     = useState('loading');
  const [expanded,   setExpanded]   = useState({ 0: true });
  const [generating, setGenerating] = useState(false);
  const [genMsg,     setGenMsg]     = useState('');
  const [lastGenTime, setLastGenTime] = useState(null);

  const loadDays = () => {
    return getTabReports('market_analyst', 7).then(res => {
      const reports = res.reports || [];
      const live = reports.map(r => {
        let rj = {};
        try { rj = typeof r.report_json === 'string' ? JSON.parse(r.report_json) : (r.report_json || {}); } catch {}
        return { date: r.report_date, _meta: { created_at: r.updated_at || r.created_at }, _rj: rj };
      });
      setDays(live);
      setStatus(live.length ? 'ok' : 'empty');
      const latest = reports[0];
      if (latest?.updated_at || latest?.created_at) {
        try {
          const iso = (latest.updated_at || latest.created_at).replace(' ','T')+'Z';
          setLastGenTime(
            new Date(iso).toLocaleTimeString('en-IN',
              {timeZone:'Asia/Kolkata',hour:'2-digit',minute:'2-digit',hour12:false})+' IST'
          );
        } catch {}
      }
      return live;
    });
  };

  useEffect(() => { loadDays().catch(() => setStatus('empty')); }, []); // eslint-disable-line

  const handleGenerate = async () => {
    setGenerating(true);
    setGenMsg('Generating… (30–90s, Opus)');
    const prevDate = days[0]?.date || '';
    try {
      await generateTabReport('market_analyst', null);
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const res = await getTabReports('market_analyst', 1);
          const latest = res.reports?.[0];
          if (latest && (attempts > 3 || (latest.report_date || '') !== prevDate)) {
            clearInterval(poll);
            await loadDays();
            setGenerating(false);
            setGenMsg('✓ Generated!');
            setTimeout(() => setGenMsg(''), 4000);
            return;
          }
        } catch {}
        if (attempts >= 25) {
          clearInterval(poll);
          setGenerating(false);
          setGenMsg('⚠ Timed out — refresh in a minute');
        }
      }, 6000);
    } catch (e) {
      setGenerating(false);
      setGenMsg(`Error: ${e.message}`);
    }
  };

  return (
    <div>
      {/* Header + Generate button — same chrome as MarketAISection */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
        marginBottom:12,paddingBottom:8,borderBottom:'.5px solid var(--hair)'}}>
        <div>
          <span style={{fontSize:11,fontWeight:700,letterSpacing:'.07em',textTransform:'uppercase',
            color:'var(--accent-blue)'}}>
            📊 Market Analyst
          </span>
          <span style={{fontSize:11,color:'var(--ink-3)',marginLeft:6}}>
            Observational synthesis · Opus · daily after close
          </span>
          {lastGenTime && (
            <span style={{fontSize:11,color:'var(--ink-3)',marginLeft:8}}>· Generated {lastGenTime}</span>
          )}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          {genMsg && (
            <span style={{fontSize:11,fontWeight:600,
              color:genMsg.startsWith('✓')?'var(--up-field-ink)':genMsg.startsWith('⚠')?'var(--amber-ink)':'var(--ink-2)'}}>
              {genMsg}
            </span>
          )}
          <button
            onClick={handleGenerate}
            disabled={generating}
            style={{fontSize:11,fontWeight:600,padding:'3px 10px',borderRadius:4,
              border:'.5px solid var(--hair-strong)',background:'var(--surface-2)',color:'var(--ink-2)',
              cursor:generating?'default':'pointer',opacity:generating?0.6:1}}>
            {generating ? '⏳' : '⚡ Generate'}
          </button>
        </div>
      </div>

      {status === 'loading' && (
        <div style={{padding:'16px 0',textAlign:'center',color:'var(--ink-3)',fontSize:11}}>
          Loading…
        </div>
      )}
      {status === 'empty' && !generating && (
        <div style={{padding:'16px 0',textAlign:'center',color:'var(--ink-3)',fontSize:11}}>
          No reports yet — generates daily at 16:30 IST or click Generate above.
        </div>
      )}

      {days.map((day, di) => (
        <AnalystDayCard
          key={di}
          day={day}
          index={di}
          expanded={!!expanded[di]}
          onToggle={() => setExpanded(e => ({...e, [di]: !e[di]}))}
        />
      ))}
    </div>
  );
}

function MarketAISection() {
  const { open: openPanel } = useSidePanel();
  const [days,       setDays]       = useState([]);
  const [status,     setStatus]     = useState('loading');
  const [expanded,   setExpanded]   = useState({ 0: true });
  const [generating, setGenerating] = useState(false);
  const [genMsg,     setGenMsg]     = useState('');

  const [lastGenTime, setLastGenTime] = useState(null);

  const loadDays = () => {
    return getTabReports('strategies', 7).then(res => {
      const reports = res.reports || [];
      const live = reports.map(r => {
        let parsed = {};
        try { parsed = typeof r.report_json === 'string' ? JSON.parse(r.report_json) : r.report_json; } catch {}
        return { date: r.report_date, _meta: { created_at: r.updated_at || r.created_at }, ...parsed };
      });
      setDays(live);
      setStatus(live.length ? 'ok' : 'empty');
      // Timestamp from latest report
      const latest = reports[0];
      if (latest?.updated_at || latest?.created_at) {
        try {
          const iso = (latest.updated_at || latest.created_at).replace(' ','T')+'Z';
          const d = new Date(iso);
          setLastGenTime(d.toLocaleTimeString('en-IN',{timeZone:'Asia/Kolkata',hour:'2-digit',minute:'2-digit',hour12:false})+' IST');
        } catch {}
      }
      return live;
    });
  };

  useEffect(() => {
    loadDays().catch(() => setStatus('empty'));
  }, []); // eslint-disable-line

  const handleGenerate = async () => {
    setGenerating(true);
    setGenMsg('Generating… (20–60s)');
    const prevDate = days[0]?.date || '';
    try {
      await generateTabReport('strategies', null);
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const res = await getTabReports('strategies', 1);
          const latest = res.reports?.[0];
          if (latest && (attempts > 3 || (latest.report_date || '') !== prevDate)) {
            clearInterval(poll);
            await loadDays();
            setGenerating(false);
            setGenMsg('✓ Generated!');
            setTimeout(() => setGenMsg(''), 4000);
            return;
          }
        } catch {}
        if (attempts >= 25) {
          clearInterval(poll);
          setGenerating(false);
          setGenMsg('⚠ Timed out — refresh in a minute');
        }
      }, 6000);
    } catch (e) {
      setGenerating(false);
      setGenMsg(`Error: ${e.message}`);
    }
  };

  return (
    <div>
      {/* Header + Generate button */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
        marginBottom:12,paddingBottom:8,borderBottom:'.5px solid var(--hair)'}}>
        <div>
          <span style={{fontSize:11,fontWeight:700,letterSpacing:'.07em',textTransform:'uppercase',color:'#6a1b9a'}}>
            🎯 F&O Watchlist
          </span>
          {lastGenTime && (
            <span style={{fontSize:11,color:'var(--ink-3)',marginLeft:8}}>Generated {lastGenTime}</span>
          )}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          {genMsg && (
            <span style={{fontSize:11,fontWeight:600,
              color:genMsg.startsWith('✓')?'var(--up-field-ink)':genMsg.startsWith('⚠')?'var(--amber-ink)':'var(--down-field-ink)'}}>
              {genMsg}
            </span>
          )}
          <button
            onClick={handleGenerate}
            disabled={generating}
            style={{fontSize:11,fontWeight:600,padding:'3px 10px',borderRadius:4,
              border:'.5px solid var(--hair-strong)',background:'var(--surface-2)',color:'var(--ink-2)',
              cursor:generating?'default':'pointer',opacity:generating?0.6:1}}>
            {generating ? '⏳' : '⚡ Generate'}
          </button>
        </div>
      </div>
      {status === 'loading' && (
        <div style={{padding:'16px 0',textAlign:'center',color:'var(--ink-3)',fontSize:11}}>
          Loading F&O watchlist…
        </div>
      )}
      {status === 'empty' && !generating && (
        <div style={{padding:'16px 0',textAlign:'center',color:'var(--ink-3)',fontSize:11}}>
          No F&O watchlist yet — generates nightly at 00:30 IST or click Generate above.
        </div>
      )}
      {days.map((day, di) => {
        const longCount  = day.long?.length  || 0;
        const shortCount = day.short?.length || 0;
        const isOpen = !!expanded[di];
        return (
          <div key={di} className="card" style={{marginBottom:8,overflow:'hidden'}}>
            {/* Day header */}
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
              padding:'8px 12px',background:'var(--surface-2)',cursor:'pointer',userSelect:'none'}}
              onClick={()=>setExpanded(e=>({...e,[di]:!e[di]}))}>
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <span style={{fontSize:12,fontWeight:700,color:'var(--ink)'}}>{fmtDate(day.date)}</span>
                <span style={{fontSize:11,color:'var(--ink-3)'}}>
                  {longCount > 0
                    ? <span style={{color:'var(--up-field-ink)',marginRight:8}}>▲ {longCount} long</span>
                    : <span style={{color:'var(--ink-3)',marginRight:8}}>no longs</span>}
                  {shortCount > 0
                    ? <span style={{color:'var(--down-field-ink)'}}>▼ {shortCount} short</span>
                    : <span style={{color:'var(--ink-3)'}}>no shorts</span>}
                </span>
              </div>
              <span style={{fontSize:11,color:'var(--ink-3)',
                transform:isOpen?'rotate(180deg)':'none',transition:'transform .2s'}}>▼</span>
            </div>

            {isOpen && (
              <div style={{padding:'10px 12px'}}>
                {/* Context blurb */}
                {day.context && (
                  <div style={{fontSize:11,color:'var(--ink-2)',lineHeight:1.55,marginBottom:12,
                    padding:'7px 10px',background:'var(--surface-2)',borderRadius:4,
                    borderLeft:'2px solid #6a1b9a'}}>
                    {day.context}
                  </div>
                )}

                {/* Long + Short grid */}
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                  {/* Long ideas */}
                  <div>
                    <div style={{fontSize:11,fontWeight:700,letterSpacing:'.05em',
                      textTransform:'uppercase',color:'var(--up-field-ink)',marginBottom:7}}>
                      ▲ Long Ideas {longCount ? `(${longCount})` : ''}
                    </div>
                    {longCount > 0 ? day.long.map((s,i) => {
                      const cs = CONV_STYLE[s.conviction] || CONV_STYLE.Medium;
                      return (
                        <div key={i} className="card" style={{padding:'8px 10px',marginBottom:6}}>
                          <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:4,flexWrap:'wrap'}}>
                            <span
                              style={{fontSize:11,fontWeight:700,color:'var(--amber-ink)',cursor:'pointer',
                                textDecoration:'underline',textDecorationStyle:'dotted'}}
                              onClick={()=>openPanel({name:s.symbol,sector:s.sector||''})}>
                              {s.symbol}
                            </span>
                            <span style={{fontSize:11,color:'var(--ink-3)'}}>{s.sector}</span>
                            <span style={{fontSize:11,fontWeight:700,padding:'1px 6px',borderRadius:3,marginLeft:'auto',
                              background:cs.bg,color:cs.c,border:`.5px solid ${cs.border}`}}>
                              {s.conviction}
                            </span>
                          </div>
                          {(s.positives?.length || s.risks?.length) ? (
                            <div style={{marginTop:4}}>
                              {s.positives?.map((p,pi)=>(
                                <div key={pi} style={{display:'flex',gap:5,alignItems:'flex-start',marginBottom:2}}>
                                  <span style={{fontSize:11,color:'var(--up-field-ink)',flexShrink:0,marginTop:1}}>✓</span>
                                  <span style={{fontSize:11,color:'var(--ink)',lineHeight:1.5}}>{p}</span>
                                </div>
                              ))}
                              {s.risks?.map((r,ri)=>(
                                <div key={ri} style={{display:'flex',gap:5,alignItems:'flex-start',marginBottom:2}}>
                                  <span style={{fontSize:11,color:'var(--down-field-ink)',flexShrink:0,marginTop:1}}>⚠</span>
                                  <span style={{fontSize:11,color:'var(--ink-2)',lineHeight:1.5}}>{r}</span>
                                </div>
                              ))}
                            </div>
                          ) : s.rationale ? (
                            <div style={{fontSize:11,color:'var(--ink-2)',lineHeight:1.5}}>{s.rationale}</div>
                          ) : null}
                        </div>
                      );
                    }) : <div style={{fontSize:11,color:'var(--ink-3)',padding:'4px 0'}}>No long ideas.</div>}
                  </div>

                  {/* Short ideas */}
                  <div>
                    <div style={{fontSize:11,fontWeight:700,letterSpacing:'.05em',
                      textTransform:'uppercase',color:'var(--down-field-ink)',marginBottom:7}}>
                      ▼ Short Ideas {shortCount ? `(${shortCount})` : ''}
                    </div>
                    {shortCount > 0 ? day.short.map((s,i) => {
                      const cs = CONV_STYLE[s.conviction] || CONV_STYLE.Medium;
                      return (
                        <div key={i} className="card" style={{padding:'8px 10px',marginBottom:6}}>
                          <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:4,flexWrap:'wrap'}}>
                            <span
                              style={{fontSize:11,fontWeight:700,color:'var(--amber-ink)',cursor:'pointer',
                                textDecoration:'underline',textDecorationStyle:'dotted'}}
                              onClick={()=>openPanel({name:s.symbol,sector:s.sector||''})}>
                              {s.symbol}
                            </span>
                            <span style={{fontSize:11,color:'var(--ink-3)'}}>{s.sector}</span>
                            <span style={{fontSize:11,fontWeight:700,padding:'1px 6px',borderRadius:3,marginLeft:'auto',
                              background:cs.bg,color:cs.c,border:`.5px solid ${cs.border}`}}>
                              {s.conviction}
                            </span>
                          </div>
                          {(s.positives?.length || s.risks?.length) ? (
                            <div style={{marginTop:4}}>
                              {s.positives?.map((p,pi)=>(
                                <div key={pi} style={{display:'flex',gap:5,alignItems:'flex-start',marginBottom:2}}>
                                  <span style={{fontSize:11,color:'var(--up-field-ink)',flexShrink:0,marginTop:1}}>✓</span>
                                  <span style={{fontSize:11,color:'var(--ink)',lineHeight:1.5}}>{p}</span>
                                </div>
                              ))}
                              {s.risks?.map((r,ri)=>(
                                <div key={ri} style={{display:'flex',gap:5,alignItems:'flex-start',marginBottom:2}}>
                                  <span style={{fontSize:11,color:'var(--down-field-ink)',flexShrink:0,marginTop:1}}>⚠</span>
                                  <span style={{fontSize:11,color:'var(--ink-2)',lineHeight:1.5}}>{r}</span>
                                </div>
                              ))}
                            </div>
                          ) : s.rationale ? (
                            <div style={{fontSize:11,color:'var(--ink-2)',lineHeight:1.5}}>{s.rationale}</div>
                          ) : null}
                        </div>
                      );
                    }) : <div style={{fontSize:11,color:'var(--ink-3)',padding:'4px 0'}}>No short ideas.</div>}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── PerformanceTables — unified universe-filtered view (R1-B) ─────────────────
const UNIVERSE_PILLS = [
  { id: 'fo',      label: 'F&O Stocks' },
  { id: 'n100',    label: 'NIFTY 100' },
  { id: 'assets',  label: 'Assets' },
  { id: 'broad',   label: 'Broad Market Indices' },
  { id: 'sector',  label: 'Sector Indices' },
];

function PerformanceTables({ indicesData, n100Data, foData, assetsData, niftyCloses }) {
  const { open: openPanel } = useSidePanel();
  const [universe, setUniverse] = useState('fo');

  const allIndexRows = useMemo(() => {
    return indicesData.rows.map(apiRow => {
      const displayLabel = KITE_TO_DISPLAY[apiRow.symbol] ?? apiRow.label;
      return apiRowToTableRow({ ...apiRow, label: displayLabel }, niftyCloses);
    });
  }, [indicesData.rows, niftyCloses]);

  const rows = useMemo(() => {
    switch (universe) {
      case 'fo':     return foData.rows.map(r => apiRowToTableRow(r, niftyCloses));
      case 'n100':   return n100Data.rows.map(r => apiRowToTableRow(r, niftyCloses));
      case 'assets': return (assetsData?.rows ?? []).map(r => apiRowToTableRow(r, niftyCloses));
      case 'broad':  return allIndexRows.filter(r => BROAD_SET.has(r.name));
      case 'sector': return allIndexRows.filter(r => SECTOR_SET.has(r.name));
      default:       return [];
    }
  }, [universe, foData.rows, n100Data.rows, assetsData, allIndexRows, niftyCloses]);

  const isStockUniverse = universe === 'fo' || universe === 'n100';
  const showVsNifty     = universe === 'fo' || universe === 'n100';
  const error = foData.error || n100Data.error || indicesData.error || assetsData?.error;

  return (
    <div>
      {error && <ErrorBanner message={error} onRetry={() => {
        foData.refetch(); n100Data.refetch(); indicesData.refetch(); assetsData?.refetch?.();
      }}/>}

      {/* Universe filter pill row — sticky below sub-tab bar, above table header */}
      <div style={{
        position:'sticky', top: TOP_TABLE, zIndex: 45,
        background:'var(--paper)', borderBottom:'.5px solid var(--hair)',
        display:'flex', gap:6, flexWrap:'wrap',
        padding:'8px 0', minHeight:40, boxSizing:'border-box',
      }}>
        {UNIVERSE_PILLS.map(p => (
          <button key={p.id}
            className={`pill${universe === p.id ? ' on' : ''}`}
            onClick={() => setUniverse(p.id)}
            style={{ fontSize: 11, padding: '3px 12px' }}>
            {p.label}
          </button>
        ))}
      </div>

      <PerfTable
        rows={rows}
        showVsNifty={showVsNifty}
        sectorFilter={isStockUniverse}
        headerTop={TOP_TABLE + 40}
        onRowClick={sym => openPanel({ name: sym })}
      />
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function PerformanceTab({ initSub='', onSubChange }) {
  const { isAdmin, can } = useUser();

  // Resolve the first sub-tab the user can actually access
  const firstAccessible = TABS_DEF.find(t => isAdmin || can(t.permKey))?.v ?? 'performance';

  const [sub, setSub] = useState(() => {
    const valid = ['performance','rotation','rs','drivers','events','directory','analyst'];
    const candidate = valid.includes(initSub) ? initSub : 'performance';
    // If user lacks permission for the requested sub-tab, fall back to first accessible
    const candidateDef = TABS_DEF.find(t => t.v === candidate);
    if (candidateDef && !isAdmin && !can(candidateDef.permKey)) {
      return TABS_DEF.find(t => isAdmin || can(t.permKey))?.v ?? candidate;
    }
    return candidate;
  });
  const handleSubChange = (s) => { setSub(s); onSubChange && onSubChange(s); };

  // Sync when sidebar navigation changes initSub externally
  useEffect(() => {
    const valid = ['performance','rotation','rs','drivers','events','directory','analyst'];
    if (initSub && valid.includes(initSub)) setSub(initSub);
  }, [initSub]);

  // ── All data lifted here — fetched once, shared across sub-tabs ──────────
  const indicesData = usePerfData('indices');
  const n100Data    = usePerfData('nifty100');
  const foData      = usePerfData('fo');
  const assetsData  = usePerfData('assets');

  const niftyCloses = useMemo(() => {
    const r = indicesData.rows.find(r => r.symbol === 'NIFTY 50');
    return r?.closes ?? [];
  }, [indicesData.rows]);

  // Symbol universe for watchlist autocomplete — built from already-loaded data
  // (indices + nifty100 + fo only — we only have price data for these)
  const [symbolUniverse, setSymbolUniverse] = useState([]);
  useEffect(() => {
    const seen = new Set();
    const list = [];
    [...indicesData.rows, ...n100Data.rows, ...foData.rows].forEach(r => {
      if (r.symbol && !seen.has(r.symbol)) {
        seen.add(r.symbol);
        list.push({ symbol: r.symbol, label: r.label || r.symbol });
      }
    });
    if (list.length > 0) setSymbolUniverse(list);
  }, [indicesData.rows, n100Data.rows, foData.rows]);

  const isRefreshing = indicesData.loading || n100Data.loading || foData.loading || assetsData.loading;
  const lastRefreshed = indicesData.lastRefreshed || n100Data.lastRefreshed || foData.lastRefreshed || assetsData.lastRefreshed;

  const handleRefresh = useCallback(() => {
    _cacheClear();
    clearRSCache();   // also bust the RS asset/stock cache
    indicesData.refetch();
    n100Data.refetch();
    foData.refetch();
    assetsData.refetch();
  }, [indicesData, n100Data, foData, assetsData]);

  return (
    <div>
      <StickySubTabs
        val={sub}
        onChange={handleSubChange}
        onRefresh={handleRefresh}
        refreshing={isRefreshing}
        lastRefreshed={lastRefreshed}
      />
      <div style={{paddingTop:6}}>
        {sub==='performance' && <PerformanceTables indicesData={indicesData} n100Data={n100Data} foData={foData} assetsData={assetsData} niftyCloses={niftyCloses}/>}
        {sub==='rotation'    && <SectorRotationTab/>}
        {sub==='rs'          && <RelativeStrengthTab/>}
        {sub==='drivers'     && <MarketMoversTab/>}
        {sub==='events'      && <MacroEventsCalendarTab/>}
        {sub==='directory'   && <NSEDirectoryTab/>}
        {sub==='analyst'     && <MarketAnalystSection/>}
      </div>
    </div>
  );
}
